import { produce } from 'immer'
import { z } from 'zod'

import {
  type FiveSegmentColumnKey,
  type FiveSegmentDisplayColumnKey,
  type FiveSegmentSortColumnKey,
  type NineSegmentColumnKey,
  type NineSegmentDisplayColumnKey,
  type NineSegmentSortColumnKey,
} from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
import { dataDisplayTypes, exists, segmentDisplayTypes } from '~/utils'

import { type RestoringParams } from '../types'

const indicators5Segs = [
  'all',
  'loyalVsRecognized',
  'loyalVsEstranged',
  'loyalVsGeneral',
  'generalVsEstranged',
  'generalVsRecognized',
] as const

const columnKey5SegsMap: Record<
  (typeof indicators5Segs)[number] | number,
  FiveSegmentColumnKey
> = {
  all: 'total',
  1: 'fiveSegment1',
  2: 'fiveSegment2',
  3: 'fiveSegment3',
  4: 'fiveSegment4',
  5: 'fiveSegment5',
  loyalVsRecognized: 'loyalVsRecognized',
  loyalVsEstranged: 'loyalVsEstranged',
  loyalVsGeneral: 'loyalVsGeneral',
  generalVsEstranged: 'generalVsEstranged',
  generalVsRecognized: 'generalVsRecognized',
}

const indicators9Segs = [
  'all',
  'negative',
  'positive',
  'diff',
  'currentUserNegative',
  'currentUserPositive',
  'currentUserDiff',
  'recognizedDiff',
  'estrangedDiff',
  'generalDiff',
  'loyalDiff',
] as const

const columnKey9SegsMap: Record<
  (typeof indicators9Segs)[number] | number,
  NineSegmentColumnKey
> = {
  all: 'total',
  1: 'nineSegment1',
  2: 'nineSegment2',
  3: 'nineSegment3',
  4: 'nineSegment4',
  5: 'nineSegment5',
  6: 'nineSegment6',
  7: 'nineSegment7',
  8: 'nineSegment8',
  9: 'nineSegment9',
  negative: 'totalNegative',
  positive: 'totalPositive',
  diff: 'totalDiff',
  currentUserNegative: 'currentUserNegative',
  currentUserPositive: 'currentUserPositive',
  currentUserDiff: 'currentUserDiff',
  recognizedDiff: 'recognizedDiff',
  estrangedDiff: 'estrangedDiff',
  generalDiff: 'generalDiff',
  loyalDiff: 'loyalDiff',
}

export const schema1 = z
  .object({
    version: z.undefined(),
    restorer: z.object({
      params: z.object({
        brandGroupId: z.number(),
        brandIds: z.array(z.number()),
        questionIds: z.array(z.string()),
      }),
      view: z.object({
        dataDisplayType: z.enum(dataDisplayTypes),
        segmentDisplayType: z.enum(segmentDisplayTypes),
        hiddenDisplaySwitchingTargets: z.array(z.string()),
        hiddenColumnDisplayTargetId5SegsList: z.array(
          z.union([z.enum(indicators5Segs), z.number()]),
        ),
        hiddenColumnDisplayTargetId9SegsList: z.array(
          z.union([z.enum(indicators9Segs), z.number()]),
        ),
        sortInfo5Segs: z.object({
          columnKey: z.union([z.enum(indicators5Segs), z.number()]),
          ascending: z.boolean(),
        }),
        sortInfo9Segs: z.object({
          columnKey: z.union([z.enum(indicators9Segs), z.number()]),
          ascending: z.boolean(),
        }),
      }),
    }),
    scroller: z.object({
      hash: z.string(),
    }),
  })
  .deepPartial()
  .transform((value) =>
    produce<Partial<RestoringParams>>({}, (draft) => {
      draft.brandGroupId = value.restorer?.params?.brandGroupId

      draft.dataDisplayType = value.restorer?.view?.dataDisplayType
      draft.segmentDisplayType = value.restorer?.view?.segmentDisplayType
      draft.brandIds = value.restorer?.params?.brandIds
      draft.questionIds = value.restorer?.params?.questionIds

      const sortInfo5Segs = value.restorer?.view?.sortInfo5Segs
      const columnKey5Segs = sortInfo5Segs?.columnKey
      const ascending5Segs = sortInfo5Segs?.ascending
      if (exists(columnKey5Segs) && exists(ascending5Segs)) {
        draft.fiveSegmentSortValue = {
          columnKey: columnKey5SegsMap[
            columnKey5Segs
          ] as FiveSegmentSortColumnKey,
          ascending: ascending5Segs,
        }
      }

      const sortInfo9Segs = value.restorer?.view?.sortInfo9Segs
      const columnKey9Segs = sortInfo9Segs?.columnKey
      const ascending9Segs = sortInfo9Segs?.ascending
      if (exists(columnKey9Segs) && exists(ascending9Segs)) {
        draft.nineSegmentSortValue = {
          columnKey: columnKey9SegsMap[
            columnKey9Segs
          ] as NineSegmentSortColumnKey,
          ascending: ascending9Segs,
        }
      }

      draft.hiddenRowIds = value.restorer?.view?.hiddenDisplaySwitchingTargets

      draft.fiveSegmentHiddenColumnKeys =
        value.restorer?.view?.hiddenColumnDisplayTargetId5SegsList?.map(
          (columnKey5Segs) =>
            columnKey5SegsMap[columnKey5Segs] as FiveSegmentDisplayColumnKey,
        )
      draft.nineSegmentHiddenColumnKeys =
        value.restorer?.view?.hiddenColumnDisplayTargetId9SegsList?.map(
          (columnKey9Segs) =>
            columnKey9SegsMap[columnKey9Segs] as NineSegmentDisplayColumnKey,
        )

      draft.hash = value.scroller?.hash
    }),
  )
